import { makeAutoObservable, runInAction } from "mobx";
import axios from "axios";
import _get from "lodash.get";

import { USER_SERVICE_URL, API_URL } from "../config/env";
import AuthUserService from "../services/AuthUserService";
import { formatBGPhone } from "../utils/functions";
import { bgPhonePrefix } from "../config/activeProducts/helpers";
import { initialPersonalData, initialPersonalDataErros, initDisableSaveBtnObj } from "../config/pages/MyProfile.page.config";

// Shared store for the "My Profile" section.
// Previously all of the profile data lived in a single query-param driven page.
// Now that the section is split into real routes, this store fetches the user
// record once (GET /user/:uid) and caches it so the personal-data / insured /
// delivery / cards pages all reuse the same data and the same save action.
export class ProfileStore {
  loading = true;

  error = false;

  loaded = false;

  token = "";

  formData = initialPersonalData;

  formDataErrors = initialPersonalDataErros;

  disableSaveBtn = initDisableSaveBtnObj;

  cars = [];

  persons = [];

  cards = [];

  marketingConsent = undefined;

  deliveryData = "";

  cityData = {};

  cityFormated = "";

  initLoadedData = { initCars: [], initPersons: [] };

  constructor() {
    makeAutoObservable(this);
  }

  set(name, value) {
    this[name] = value;
  }

  setFormData(updater) {
    this.formData = typeof updater === "function" ? updater(this.formData) : updater;
  }

  setFormDataErrors(updater) {
    this.formDataErrors = typeof updater === "function" ? updater(this.formDataErrors) : updater;
  }

  setDisableSaveBtn(updater) {
    this.disableSaveBtn = typeof updater === "function" ? updater(this.disableSaveBtn) : updater;
  }

  transformDisableSaveBtn(modifiedFieldKey, isChanged) {
    this.disableSaveBtn = { ...this.disableSaveBtn, [modifiedFieldKey]: isChanged };
  }

  resetDisableSaveBtn() {
    this.disableSaveBtn = initDisableSaveBtnObj;
  }

  async getSiteSuggestions(user, search) {
    const token = await AuthUserService.getUserToken(user);

    return axios
      .get(`${API_URL}/postcodes`, {
        params: { name: search },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(({ data }) => data);
  }

  // Fetch the user record. Guarded by `loaded` so navigating between the
  // profile sub-pages reuses the cached data; pass { force: true } to refetch
  // (used when (re)entering the section hub or after a save).
  async load(user, { force = false } = {}) {
    if (!user) {
      return;
    }

    if (this.loaded && !force) {
      runInAction(() => {
        this.loading = false;
      });
      return;
    }

    runInAction(() => {
      this.loading = true;
      this.error = false;
    });

    try {
      const token = await AuthUserService.getUserToken(user);
      const response = await axios.get(`${USER_SERVICE_URL}/${user.uid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data[0];

      runInAction(() => {
        this.token = token;
      });

      if (data) {
        const preformattedPhone = formatBGPhone(data?.phone);

        runInAction(() => {
          this.formData = {
            email: data.email,
            firstName: data.firstName,
            middleNames: data.middleNames,
            lastName: data.lastName,
            egn: data.egn,
            mobileNumber: preformattedPhone,
            city: data.city,
            address: data.address
          };
          this.marketingConsent = data.marketingConsent;
          this.deliveryData = data.deliveryData;
          this.cityData = data.cityData || {};
          this.cars = _get(data, "insuredObjects.cars", []);
          this.persons = _get(data, "insuredObjects.persons", []);
          this.cards = _get(data, "insuredObjects.cards", []);
          this.initLoadedData = {
            initMarketingConsent: data.marketingConsent,
            initFirstName: data.firstName,
            initMiddleNames: data.middleNames,
            initSurname: data.lastName,
            initEgn: data.egn,
            initEmail: data.email,
            initPhone: data.phone,
            initAddress: data.address,
            initDeliveryData: data.deliveryData,
            initCity: data.city,
            initCityData: data.cityData,
            initCars: _get(data, "insuredObjects.cars", []),
            initPersons: _get(data, "insuredObjects.persons", []),
            initCards: _get(data, "insuredObjects.cards", [])
          };
        });

        // Legacy behaviour: if the stored city has no postcode data, try to
        // resolve it from the postcodes service and lock the formatted value.
        if (data.city && !data.cityData?.id) {
          try {
            const resp = await this.getSiteSuggestions(user, data.city);
            const firstItem = resp?.[0];
            if (firstItem) {
              runInAction(() => {
                this.formData = { ...this.formData, city: firstItem?.name };
                this.cityData = firstItem;
                this.cityFormated = `[${firstItem?.postCode}] ${firstItem?.city}, ${firstItem?.region}`;
              });
            }
          } catch (err) {
            runInAction(() => {
              this.error = true;
            });
          }
        }
      }

      runInAction(() => {
        this.loaded = true;
        this.loading = false;
      });
    } catch (err) {
      console.log(err);
      runInAction(() => {
        this.error = true;
        this.loading = false;
      });
    }
  }

  // Posts the full profile record (personal data + insured objects + consent).
  // Every save page (personal-data / insured / cards / delivery) shares this so
  // a save from any page always sends a complete, current object.
  async save(user) {
    if (!user) {
      return false;
    }

    runInAction(() => {
      this.loading = true;
    });

    try {
      const token = await user.getIdToken();

      const postData = {
        user: {
          uid: user.uid,
          firstName: this.formData.firstName,
          middleNames: this.formData.middleNames,
          lastName: this.formData.lastName,
          egn: this.formData.egn,
          email: this.formData.email,
          phone: `${bgPhonePrefix}${formatBGPhone(this.formData.mobileNumber)}`,
          address: this.formData.address,
          city: this.formData.city,
          cityData: this.cityData,
          insuredObjects: {
            cars: [...this.cars],
            persons: [...this.persons],
            cards: [...this.cards]
          },
          marketingConsent: this.marketingConsent
        },
        type: "profile",
        strategy: "replace"
      };

      await axios.post(USER_SERVICE_URL, postData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await AuthUserService.setUserInfo(`${this.formData.firstName} ${this.formData.lastName}`);

      runInAction(() => {
        this.loading = false;
        this.resetDisableSaveBtn();
        // Force the next load() to refetch the freshly saved record.
        this.loaded = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this.error = true;
        this.loading = false;
      });

      return false;
    }
  }

  reset = () => {
    this.loading = true;
    this.error = false;
    this.loaded = false;
    this.token = "";
    this.formData = initialPersonalData;
    this.formDataErrors = initialPersonalDataErros;
    this.disableSaveBtn = initDisableSaveBtnObj;
    this.cars = [];
    this.persons = [];
    this.cards = [];
    this.marketingConsent = undefined;
    this.deliveryData = "";
    this.cityData = {};
    this.cityFormated = "";
    this.initLoadedData = { initCars: [], initPersons: [] };
  };
}

const profile = new ProfileStore();
export default profile;
